/* ==========================================================================
   Kopstorm PDP V4 (Anavrin, 8 Oct 2026)
   Behaviour for the V4 product page sections. Every part is bound to a data
   attribute inside its own section, so a section that is not on the page
   costs nothing, and re-rendering a section in the editor re-binds it.
   ========================================================================== */
(function () {
  'use strict';

  // Every V4 section links this file, so guard against running twice.
  if (window.KopstormPDPv4) return;
  window.KopstormPDPv4 = true;

  function each(list, fn) {
    Array.prototype.forEach.call(list, fn);
  }

  /* ---------- Splide: drag sliders (14 Oct 2026) ----------
     Splide 4.1.4 ships as a theme asset next to this file and only loads
     on pages with a V4 section, after the page is interactive. Until it is
     there, every row stays a native swipe row, so nothing waits on it. */
  var KP4_BASE = '';
  try {
    var kp4Script = document.currentScript && document.currentScript.src;
    if (kp4Script) KP4_BASE = kp4Script.split('kopstorm-pdp-v4.js')[0];
  } catch (e) {}

  function loadSplide() {
    if (window.Splide) return Promise.resolve();
    if (!window.__kopSplidePromise) {
      window.__kopSplidePromise = new Promise(function (resolve, reject) {
        if (!KP4_BASE) { reject(new Error('no asset base')); return; }
        var css = document.createElement('link');
        css.rel = 'stylesheet';
        css.href = KP4_BASE + 'kopstorm-splide-core.min.css';
        document.head.appendChild(css);
        var js = document.createElement('script');
        js.src = KP4_BASE + 'kopstorm-splide.min.js';
        js.async = true;
        js.onload = function () { window.Splide ? resolve() : reject(new Error('splide')); };
        js.onerror = reject;
        document.head.appendChild(js);
      });
    }
    return window.__kopSplidePromise;
  }

  // Wraps a row in the Splide root and track once. The row itself becomes the
  // list, so the theme markup and the no JS swipe row stay exactly the same.
  function kp4Wrap(el, extraClass) {
    if (el.kp4Root) return el.kp4Root;
    // Read the row's own top margin before Splide's list reset removes it.
    el.kp4Margin = getComputedStyle(el).marginTop;
    // The outer box carries the spacing, because Splide resets the root's
    // inline style on every refresh.
    var box = document.createElement('div');
    box.className = 'kp4-splide-box';
    box.style.marginTop = el.kp4Margin;
    var root = document.createElement('div');
    root.className = 'splide kp4-splide' + (extraClass ? ' ' + extraClass : '');
    var track = document.createElement('div');
    track.className = 'splide__track';
    el.parentNode.insertBefore(box, el);
    box.appendChild(root);
    root.appendChild(track);
    track.appendChild(el);
    el.classList.add('splide__list');
    el.kp4Root = root;
    return root;
  }

  function kp4Slides(el) {
    each(el.children, function (c) { c.classList.add('splide__slide'); });
  }

  // Puts the row back exactly as the theme rendered it (desktop grids).
  function kp4Unwrap(el) {
    var root = el.kp4Root;
    if (!root) return;
    var box = root.parentNode;
    box.parentNode.insertBefore(el, box);
    box.remove();
    el.classList.remove('splide__list');
    each(el.children, function (c) { c.classList.remove('splide__slide'); });
    el.kp4Root = null;
  }

  /* Mounts a slider on a row. mobileOnly rows go back to the theme grid from
     768px up. onMount receives the instance, every remount included. */
  function kp4Slider(el, options, mobileOnly, extraClass, onMount) {
    if (!el || el.kp4SliderBound) return;
    el.kp4SliderBound = true;
    var mq = window.matchMedia('(min-width: 768px)');
    var instance = null;

    function mount() {
      if (instance || (mobileOnly && mq.matches)) return;
      var root = kp4Wrap(el, extraClass);
      kp4Slides(el);
      if (!el.children.length) return;
      instance = new window.Splide(root, options).mount();
      el.kp4Splide = instance;
      if (onMount) onMount(instance);
    }

    function unmount() {
      if (!instance) return;
      instance.destroy(true);
      instance = null;
      el.kp4Splide = null;
      kp4Unwrap(el);
      if (el.kp4AfterUnmount) el.kp4AfterUnmount();
    }

    el.kp4Remount = function () { unmount(); mount(); };
    el.kp4Unmount = unmount;

    loadSplide().then(function () {
      mount();
      if (mobileOnly && mq.addEventListener) {
        mq.addEventListener('change', function () { mq.matches ? unmount() : mount(); });
      }
    }).catch(function () { /* native swipe row stays */ });
  }

  var KP4_BLEED = { left: 'var(--page-padding)', right: 'var(--page-padding)' };

  /* ---------- Gallery: thumbs, dots, lights toggle ---------- */
  function initGallery(root) {
    var gallery = root.querySelector('[data-kp4-gallery]');
    if (!gallery || gallery.dataset.kp4Ready) return;
    gallery.dataset.kp4Ready = '1';

    var track = gallery.querySelector('[data-kp4-track]');
    var slides = gallery.querySelectorAll('[data-kp4-slide]');
    var thumbs = gallery.querySelectorAll('[data-kp4-thumb]');
    var dots = gallery.querySelectorAll('[data-kp4-dot]');
    var count = gallery.querySelector('[data-kp4-count]');
    var progress = gallery.querySelector('[data-kp4-progress]');
    var prevs = gallery.querySelectorAll('[data-kp4-prev]');
    var nexts = gallery.querySelectorAll('[data-kp4-next]');
    var current = 0;
    if (!track || !slides.length) return;

    var splide = null;

    /* The See it in action pill sits on the photo itself, not on the progress
       line under it: the stage gets the photo height as --kp4-photo-h. */
    var stage = gallery.querySelector('.kp4-gallery__stage');
    function syncPhotoHeight() {
      if (!stage) return;
      var box = track.closest('.kp4-splide-box') || track;
      var h = box.offsetHeight;
      if (h) stage.style.setProperty('--kp4-photo-h', h + 'px');
    }
    if (stage && stage.querySelector('.kop-pv-open')) {
      syncPhotoHeight();
      window.addEventListener('load', syncPhotoHeight);
      window.addEventListener('resize', syncPhotoHeight);
      if (window.ResizeObserver) new ResizeObserver(syncPhotoHeight).observe(track);
    }

    function goTo(index) {
      if (splide) { splide.go(index); return; }
      var slide = slides[index];
      if (!slide) return;
      track.scrollTo({ left: slide.offsetLeft - track.offsetLeft, behavior: 'smooth' });
    }

    function setActive(index) {
      each(thumbs, function (t) {
        t.setAttribute('aria-current', Number(t.dataset.kp4Thumb) === index ? 'true' : 'false');
      });
      each(dots, function (d, i) {
        d.classList.toggle('is-active', i === Math.min(index, dots.length - 1));
      });
      if (count) {
        var pad = function (n) { return n < 10 ? '0' + n : String(n); };
        count.innerHTML = '<strong>' + pad(index + 1) + '</strong><span>/ ' + pad(slides.length) + '</span>';
      }
      if (progress) {
        progress.style.transform = 'scaleX(' + (index + 1) / slides.length + ')';
      }
      current = index;
      // Keep the active thumbnail in view inside the thumbnail column
      var active = gallery.querySelector('[data-kp4-thumb="' + index + '"]');
      var col = active ? active.parentNode : null;
      if (col && col.scrollHeight > col.clientHeight) {
        var top = active.offsetTop - col.offsetTop;
        if (top < col.scrollTop) col.scrollTo({ top: top - 2, behavior: 'smooth' });
        else if (top + active.offsetHeight > col.scrollTop + col.clientHeight) col.scrollTo({ top: top + active.offsetHeight - col.clientHeight + 2, behavior: 'smooth' });
      }
      each(prevs, function (b) { b.disabled = index <= 0; });
      each(nexts, function (b) { b.disabled = index >= slides.length - 1; });
    }

    each(prevs, function (b) {
      b.addEventListener('click', function () { goTo(Math.max(0, current - 1)); });
    });
    each(nexts, function (b) {
      b.addEventListener('click', function () { goTo(Math.min(slides.length - 1, current + 1)); });
    });

    each(thumbs, function (t) {
      t.addEventListener('click', function () {
        goTo(Number(t.dataset.kp4Thumb));
      });
    });

    var ticking = false;
    track.addEventListener('scroll', function () {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(function () {
        ticking = false;
        var index = Math.round(track.scrollLeft / Math.max(track.clientWidth, 1));
        setActive(Math.max(0, Math.min(index, slides.length - 1)));
      });
    }, { passive: true });

    var lights = gallery.querySelectorAll('[data-kp4-lights]');
    each(lights, function (btn) {
      btn.addEventListener('click', function () {
        var off = btn.dataset.kp4Lights === 'off';
        gallery.classList.toggle('kp4-lights-off', off);
        each(lights, function (b) {
          b.setAttribute('aria-pressed', b === btn ? 'true' : 'false');
        });
        goTo(0);
      });
    });

    // The thumbnail column is as tall as the photo and scrolls when there are more
    var thumbCol = gallery.querySelector('.kp4-gallery__thumbs');
    var stage = gallery.querySelector('.kp4-gallery__stage');
    function sizeThumbs() {
      if (!thumbCol || !stage) return;
      var media = stage.querySelector('[data-kp4-slide]');
      thumbCol.style.maxHeight = media ? media.offsetHeight + 'px' : '';
    }
    sizeThumbs();
    window.addEventListener('resize', sizeThumbs);
    window.addEventListener('load', sizeThumbs);

    setActive(0);

    if (slides.length > 1) {
      kp4Slider(track, {
        type: 'slide',
        perPage: 1,
        gap: '8px',
        arrows: false,
        pagination: false,
        speed: 450,
        easing: 'cubic-bezier(0.25, 1, 0.5, 1)',
        dragMinThreshold: { mouse: 4, touch: 10 },
        flickPower: 400,
        keyboard: 'focused'
      }, false, 'kp4-splide--gallery', function (instance) {
        splide = instance;
        instance.on('move', function (index) { setActive(index); });
        setActive(instance.index);
      });
    }
  }

  /* ---------- Drag rows: evenings, builders also added ---------- */
  function initRails(root) {
    each(root.querySelectorAll('.kp4-rail'), function (rail) {
      kp4Slider(rail, {
        type: 'slide',
        fixedWidth: '72%',
        gap: 'var(--sp-3)',
        padding: KP4_BLEED,
        arrows: false,
        pagination: false,
        drag: true,
        snap: true,
        trimSpace: 'move',
        flickPower: 400
      }, true, 'kp4-splide--bleed');
    });
    each(root.querySelectorAll('.kp4-ups__row'), function (row) {
      kp4Slider(row, {
        type: 'slide',
        fixedWidth: 'calc((100% - 2 * var(--sp-2d5)) / 2.6)',
        gap: 'var(--sp-2d5)',
        padding: KP4_BLEED,
        arrows: false,
        pagination: false,
        trimSpace: 'move',
        flickPower: 400,
        mediaQuery: 'min',
        breakpoints: {
          768: { fixedWidth: '24%', padding: 0 },
          1024: { fixedWidth: 'calc((100% - 3 * var(--sp-2d5)) / 4)', padding: 0 }
        }
      }, false, 'kp4-splide--bleed kp4-splide--ups', function (instance) {
        var box = row.kp4Root ? row.kp4Root.parentNode.parentNode : null;
        var bar = box ? box.querySelector('.kp4-ups__progress') : null;
        var fill = bar ? bar.querySelector('[data-kp4-ups-progress]') : null;
        if (!fill) return;
        bar.classList.add('kp4-line--thumb');
        var trackEl = row.parentNode;
        function update(dest) {
          // A scrollbar style thumb: as wide as the share of the row in view,
          // sliding along the track to where the row is heading.
          var view = trackEl.clientWidth;
          var full = row.scrollWidth;
          var last = row.lastElementChild;
          if (last) full = Math.max(full, last.offsetLeft + last.offsetWidth);
          var hidden = full - view;
          bar.hidden = hidden < 4;
          if (hidden < 4) return;
          var share = Math.max(0.12, Math.min(1, view / full));
          var slides = instance.Components.Slides;
          var target = typeof dest === 'number' ? dest : instance.index;
          var slide = slides.getAt(target);
          var offset = slide ? slide.slide.offsetLeft : 0;
          var pos = Math.max(0, Math.min(1, offset / hidden));
          fill.style.width = (share * 100) + '%';
          fill.style.transform = 'translateX(' + (pos * (1 - share) / share * 100) + '%)';
        }
        instance.on('move', function (dest) { update(dest); });
        instance.on('resized refresh', function () { update(); });
        update();
      });
    });
  }

  /* ---------- One price in the add to cart button ----------
     The theme prints the price in the button. An app (Kaching) appends a
     second copy as loose text at runtime, so loose text with a number in it
     is cleared from the button whenever it appears. */
  function initBuyPrice(root) {
    each(root.querySelectorAll('[data-kp4-buy] .product-form__submit .btn-text'), function (host) {
      if (host.kp4PriceWatch) return;
      host.kp4PriceWatch = true;
      var label = host.querySelector('.product-form__submit-label');
      var labelText = label ? (label.getAttribute('data-kop6-atclabel') || label.textContent).trim() : '';
      var busy = false;
      // Anything an app adds goes, wherever it lands: loose text in the
      // button, extra text in the label, or extra nodes next to the price.
      function clean() {
        if (busy) return;
        busy = true;
        each(Array.prototype.slice.call(host.childNodes), function (node) {
          if (node.nodeType === 3 && /\d/.test(node.textContent)) node.textContent = '';
        });
        if (label && labelText && label.textContent.trim() !== labelText) label.textContent = labelText;
        var native = host.querySelector('[id^="BuyButtonPrice"]');
        if (native) {
          var price = native.querySelector('product-buy-price');
          each(Array.prototype.slice.call(native.childNodes), function (node) {
            if (node === price || (node.nodeType === 1 && node.tagName === 'SPAN' && node === native.firstElementChild)) return;
            if (node.nodeType === 3 && !/\d/.test(node.textContent)) return;
            node.parentNode.removeChild(node);
          });
        }
        busy = false;
      }
      clean();
      new MutationObserver(clean).observe(host, { childList: true, subtree: true, characterData: true });
    });
  }

  /* ---------- Delivery estimate ---------- */
  function addBusinessDays(date, days) {
    var d = new Date(date.getTime());
    var added = 0;
    while (added < days) {
      d.setDate(d.getDate() + 1);
      var day = d.getDay();
      if (day !== 0 && day !== 6) added++;
    }
    return d;
  }

  function initDelivery(root) {
    var el = root.querySelector('[data-kp4-delivery]');
    if (!el || el.dataset.kp4Ready) return;
    el.dataset.kp4Ready = '1';
    var min = Number(el.dataset.min) || 0;
    var max = Number(el.dataset.max) || 0;
    if (!min || !max) return;
    var locale = document.documentElement.lang || 'en';
    var fmt;
    try {
      fmt = new Intl.DateTimeFormat(locale, { month: 'short', day: 'numeric' });
    } catch (e) {
      fmt = new Intl.DateTimeFormat('en', { month: 'short', day: 'numeric' });
    }
    var now = new Date();
    var start = el.querySelector('[data-kp4-start]');
    var end = el.querySelector('[data-kp4-end]');
    if (start) start.textContent = fmt.format(addBusinessDays(now, min));
    if (end) end.textContent = fmt.format(addBusinessDays(now, max));
    el.hidden = false;
  }

  /* ---------- Mobile sticky add to cart ---------- */
  function initSticky(root) {
    var bar = root.querySelector('[data-kp4-sticky]');
    var target = root.querySelector('[data-kp4-buy]');
    if (!bar || !target || bar.dataset.kp4Ready) return;
    bar.dataset.kp4Ready = '1';
    // A scroll check rather than an IntersectionObserver: a fast jump from
    // above the buy button to below it never intersects, so an observer
    // would not fire and the bar would stay hidden.
    var ticking = false;
    function update() {
      ticking = false;
      var r = target.getBoundingClientRect();
      bar.classList.toggle('is-visible', r.bottom < 0);
    }
    window.addEventListener('scroll', function () {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(update);
    }, { passive: true });
    update();
  }

  /* ---------- Review wall: filters and show more ---------- */
  function initReviews(root) {
    var wall = root.querySelector('[data-kp4-wall]');
    if (!wall || wall.dataset.kp4Ready) return;
    wall.dataset.kp4Ready = '1';

    var cards = wall.querySelectorAll('[data-kp4-review]');
    var chips = root.querySelectorAll('[data-kp4-chip]');
    var more = root.querySelector('[data-kp4-more]');
    var mq = window.matchMedia('(min-width: 768px)');
    var limitDesk = Number(wall.dataset.limit) || 6;
    var filter = 'all';
    var expanded = false;

    cards = Array.prototype.slice.call(cards);

    function render() {
      // Mobile is a drag slider, so every matching review stays in it.
      var limit = mq.matches ? limitDesk : 999;
      // 19 Oct 2026: Show more on desktop only adds whole rows, so a single
      // card never sits alone on the last row.
      if (expanded && mq.matches) {
        var cols = parseInt(getComputedStyle(wall).getPropertyValue('--kp4-wall-cols'), 10) || 4;
        var total = 0;
        each(cards, function (c) {
          var t = (c.dataset.tags || '').split('|');
          if (filter === 'all' || t.indexOf(filter) > -1) total++;
        });
        limit = Math.max(limitDesk, Math.floor(total / cols) * cols);
      }
      var shown = 0;
      var matching = 0;
      var sliding = !!wall.kp4Splide;
      var keep = [];
      each(cards, function (card) {
        var tags = (card.dataset.tags || '').split('|');
        var match = filter === 'all' || tags.indexOf(filter) > -1;
        if (match) matching++;
        var visible = match && shown < limit;
        if (visible) shown++;
        card.hidden = !visible;
        if (!sliding || visible) keep.push(card);
      });
      // Splide counts every child as a slide, so filtered cards leave the list.
      if (sliding) {
        wall.replaceChildren.apply(wall, keep);
        wall.kp4Splide.refresh();
        wall.kp4Splide.go(0);
      } else if (wall.children.length !== cards.length) {
        wall.replaceChildren.apply(wall, cards);
      }
      if (more) more.hidden = expanded || matching <= limit || (mq.matches && Math.floor(matching / (parseInt(getComputedStyle(wall).getPropertyValue('--kp4-wall-cols'), 10) || 4)) * (parseInt(getComputedStyle(wall).getPropertyValue('--kp4-wall-cols'), 10) || 4) <= limit);
    }

    each(chips, function (chip) {
      chip.addEventListener('click', function () {
        filter = chip.dataset.kp4Chip;
        each(chips, function (c) {
          c.setAttribute('aria-pressed', c === chip ? 'true' : 'false');
        });
        render();
      });
    });

    if (more) {
      var btn = more.querySelector('button');
      if (btn) {
        btn.addEventListener('click', function () {
          expanded = true;
          render();
        });
      }
    }

    if (mq.addEventListener) mq.addEventListener('change', render);
    render();

    kp4Slider(wall, {
      type: 'slide',
      fixedWidth: '70%',
      gap: 'var(--sp-3)',
      padding: KP4_BLEED,
      arrows: false,
      pagination: false,
      trimSpace: 'move',
      flickPower: 400
    }, true, 'kp4-splide--bleed', function () { render(); });
    wall.kp4AfterUnmount = render;
  }


  /* ---------- Review wall: live Loox photo reviews of this kit ---------- */
  var LOOX_CACHE_MS = 60 * 60 * 1000;

  function looxShorten(text) {
    text = text.replace(/\s+/g, ' ').trim();
    if (text.length <= 180) return text;
    var cut = text.slice(0, 180);
    var end = cut.search(/[.!?][^.!?]*$/);
    if (end > 60) return cut.slice(0, end + 1);
    return cut.replace(/\s+\S*$/, '').replace(/[,;:]$/, '') + '...';
  }

  function looxImage(src) {
    if (!src) return '';
    if (src.indexOf('//') === 0) src = 'https:' + src;
    if (/_mid\.(jpe?g|png|webp)$/i.test(src)) return src;
    return src.replace(/\.(jpe?g|png|webp)$/i, '_mid.$1');
  }

  // Only reviews whose photo belongs to this kit, newest first, 5 stars.
  // 19 Oct 2026: the page title is translated (Boekenhoekset Seoul Hipjiro)
  // while Loox photo alts keep the English name, so on every storefront
  // except English no review matched. The kit name now comes from the
  // handle, which is the same in every language.
  function looxKey(handle) {
    var skip = { book: 1, nook: 1, kit: 1, miniature: 1, house: 1, diy: 1, anavrin: 1, lighted: 1, modular: 1, bricks: 1, music: 1, box: 1, limited: 1, edition: 1 };
    return (handle || '').toLowerCase().split('-').filter(function (w) {
      return w && !skip[w] && !/^\d+$/.test(w);
    });
  }

  function looxParse(html, title, max, handle) {
    var doc = new DOMParser().parseFromString(html, 'text/html');
    var titleLow = (title || '').toLowerCase();
    var keyWords = looxKey(handle);
    var out = [];
    // 19 Oct 2026: new kits have mostly video reviews, so the video poster
    // frame is used as a still when there are not enough photo reviews.
    var vids = [];
    var items = doc.querySelectorAll('[data-id].grid-item-wrap, [data-id].has-img');
    if (!items.length) items = doc.querySelectorAll('.grid-item');
    each(items, function (item) {
      if (out.length >= max) return;
      var id = item.getAttribute('data-id') || '';
      var img = item.querySelector('[data-testid="review-' + id + '-media"] img') || item.querySelector('img[src*="loox.io"]');
      var textEl = item.querySelector('[data-testid="review-' + id + '-text"]') || item.querySelector('.main-text');
      var nameEl = item.querySelector('[data-testid="review-' + id + '-title"]') || item.querySelector('.title');
      var starsEl = item.querySelector('[data-testid="review-' + id + '-stars"] [aria-label]') || item.querySelector('.stars [aria-label]');
      if (!img || !textEl || !nameEl) return;
      var isVideo = item.getAttribute('data-media-type') === 'video';
      if (starsEl && !/5\s*\/\s*5/.test(starsEl.getAttribute('aria-label') || '')) return;
      var alt = (img.getAttribute('alt') || '').toLowerCase();
      var match = keyWords.length
        ? keyWords.every(function (w) { return alt.indexOf(w) !== -1; })
        : (titleLow && alt.indexOf(titleLow) !== -1);
      if (!match) return;
      var name = (nameEl.firstChild && nameEl.firstChild.nodeType === 3 ? nameEl.firstChild.textContent : nameEl.textContent).trim();
      var text = textEl.textContent.trim();
      if (!name || text.length < 25) return;
      var src = img.getAttribute('src') || '';
      if (src.indexOf('//') === 0) src = 'https:' + src;
      var entry = { name: looxName(name), text: looxShorten(text), photo: isVideo ? src : looxImage(img.getAttribute('src')) };
      // 19 Oct 2026: Loox video posters carry a baked-in play icon, so video
      // reviews are not used as stills.
      if (!isVideo) out.push(entry);
    });
    return out.concat(vids).slice(0, max);
  }

  // Emily Watson -> Emily W. (first name plus initial, as on the Loox widget)
  function looxName(n) {
    var parts = n.replace(/\s+/g, ' ').trim().split(' ');
    if (parts.length < 2) return parts[0] || n;
    var last = parts[parts.length - 1].replace(/\.$/, '');
    return parts[0] + ' ' + last.charAt(0).toUpperCase() + '.';
  }

  var KP4_MIN_CARDS = 4;

  function looxRender(root, wall, reviews) {
    var template = wall.querySelector('[data-kp4-review]');
    if (!template || reviews.length < 1) return false;
    // The curated cards of this kit fill the row up to four when Loox has fewer
    var looxNames = reviews.map(function (r) { return (r.name || '').toLowerCase().split(' ')[0]; });
    var curated = Array.prototype.slice.call(wall.querySelectorAll('[data-kp4-review]')).filter(function (c) {
      // 19 Oct 2026: a curated card by a reviewer already shown from Loox is skipped
      var n = c.querySelector('[data-kp4-name]');
      var first = n ? n.textContent.trim().toLowerCase().split(' ')[0] : '';
      return !first || looxNames.indexOf(first) === -1;
    }).map(function (c) {
      var copy = c.cloneNode(true);
      copy.hidden = false;
      copy.dataset.tags = '';
      return copy;
    });
    var frag = document.createDocumentFragment();
    reviews.forEach(function (review) {
      var card = template.cloneNode(true);
      card.hidden = false;
      card.dataset.tags = '';
      card.removeAttribute('data-shopify-editor-block');
      var media = card.querySelector('.kp4-review__media');
      if (!media) {
        media = document.createElement('div');
        media.className = 'kp4-review__media';
        card.insertBefore(media, card.firstChild);
      }
      var img = document.createElement('img');
      img.src = review.photo;
      img.alt = review.name;
      img.width = 600;
      img.height = 600;
      img.loading = 'lazy';
      media.replaceChildren(img);
      var body = card.querySelector('.kp4-review__body');
      var ps = body ? body.querySelectorAll('p') : [];
      var quote = ps.length > 1 ? ps[ps.length - 2] : null;
      var name = ps.length ? ps[ps.length - 1] : null;
      each(body ? body.querySelectorAll('p') : [], function (p) {
        if (p !== quote && p !== name) p.remove();
      });
      if (quote) quote.textContent = review.text;
      if (name) {
        var nameEl = name.querySelector('[data-kp4-name]');
        if (nameEl) {
          nameEl.textContent = review.name;
          nameEl.hidden = false;
        } else {
          name.textContent = review.name;
        }
      }
      frag.appendChild(card);
    });
    var filled = reviews.length;
    for (var i = 0; filled < KP4_MIN_CARDS && i < curated.length; i++, filled++) {
      frag.appendChild(curated[i]);
    }
    wall.replaceChildren(frag);
    // Topic filters belong to the curated cards, so they go with them.
    each(root.querySelectorAll('.kp4-chips'), function (c) { c.hidden = true; });
    return true;
  }

  function initLoox(root, done) {
    var wall = root.querySelector('[data-kp4-wall][data-kp4-loox]');
    if (!wall || wall.dataset.kp4LooxDone || !window.fetch || !window.DOMParser) { done(); return; }
    wall.dataset.kp4LooxDone = '1';
    var proxy = (wall.getAttribute('data-kp4-loox') || '').replace(/\/+$/, '');
    var product = wall.dataset.kp4LooxProduct;
    var title = wall.dataset.kp4LooxTitle;
    var handle = wall.dataset.kp4LooxHandle;
    var max = Number(wall.dataset.kp4LooxMax) || 8;
    var key = 'kp4-loox-v5-' + product;
    function apply(reviews) {
      looxRender(root, wall, reviews);
      done();
    }
    try {
      var raw = sessionStorage.getItem(key);
      if (raw) {
        var data = JSON.parse(raw);
        if (Date.now() - data.t < LOOX_CACHE_MS) { apply(data.r); return; }
      }
    } catch (e) {}
    // 19 Oct 2026: the cards stay hidden while Loox answers, at most 1.5 s,
    // so visitors no longer see the curated cards switch to Loox photos.
    // Space stays reserved, so nothing jumps.
    var chipsEls = root.querySelectorAll('.kp4-chips');
    var waiting = true;
    wall.classList.add('kp4-wall--waiting');
    each(chipsEls, function (c) { c.classList.add('kp4-chips--waiting'); });
    function reveal() {
      if (!waiting) return;
      waiting = false;
      wall.classList.remove('kp4-wall--waiting');
      each(chipsEls, function (c) { c.classList.remove('kp4-chips--waiting'); });
    }
    var revealTimer = window.setTimeout(reveal, 1500);
    done();
    fetch(proxy + '/?product=' + encodeURIComponent(product), { credentials: 'omit' })
      .then(function (res) { if (!res.ok) throw new Error('loox ' + res.status); return res.text(); })
      .then(function (html) {
        var reviews = looxParse(html, title, max, handle);
        try { sessionStorage.setItem(key, JSON.stringify({ t: Date.now(), r: reviews })); } catch (e) {}
        if (reviews.length < 1) { window.clearTimeout(revealTimer); reveal(); return; }
        if (wall.kp4Unmount) wall.kp4Unmount();
        if (looxRender(root, wall, reviews)) {
          delete wall.dataset.kp4Ready;
          wall.kp4SliderBound = false;
          initReviews(root);
        }
        window.clearTimeout(revealTimer);
        reveal();
      })
      .catch(function () { window.clearTimeout(revealTimer); reveal(); /* keep the curated cards */ });
  }

  /* ---------- Video story: play on tap, branded controls ---------- */
  function fmtTime(t) {
    if (!isFinite(t)) t = 0;
    var m = Math.floor(t / 60);
    var sec = Math.floor(t % 60);
    return m + ':' + (sec < 10 ? '0' : '') + sec;
  }

  function initFilm(root) {
    var film = root.querySelector('[data-kp4-film]');
    if (!film || film.dataset.kp4Ready) return;
    film.dataset.kp4Ready = '1';
    var btn = film.querySelector('[data-kp4-play]');
    var video = film.querySelector('video');
    if (!btn || !video) return;
    video.removeAttribute('controls');

    var toggle = film.querySelector('[data-kp4-toggle]');
    var track = film.querySelector('[data-kp4-track-bar]');
    var fill = film.querySelector('[data-kp4-fill]');
    var time = film.querySelector('[data-kp4-time]');
    var mute = film.querySelector('[data-kp4-mute]');
    var full = film.querySelector('[data-kp4-full]');
    var idle;

    function wake() {
      film.classList.remove('is-idle');
      window.clearTimeout(idle);
      if (!video.paused) {
        idle = window.setTimeout(function () { film.classList.add('is-idle'); }, 2200);
      }
    }

    function play() {
      var p = video.play();
      if (p && p.catch) p.catch(function () {});
    }

    btn.addEventListener('click', function () {
      film.classList.add('is-playing');
      play();
      wake();
    });

    video.addEventListener('click', function () {
      if (video.paused) play(); else video.pause();
    });
    video.addEventListener('play', function () { film.classList.remove('is-paused'); wake(); });
    video.addEventListener('pause', function () { film.classList.add('is-paused'); wake(); });
    video.addEventListener('timeupdate', function () {
      var pct = video.duration ? (video.currentTime / video.duration) * 100 : 0;
      if (fill) fill.style.width = pct + '%';
      if (track) track.setAttribute('aria-valuenow', Math.round(pct));
      if (time) time.textContent = fmtTime(video.currentTime) + ' / ' + fmtTime(video.duration);
    });
    video.addEventListener('ended', function () { film.classList.add('is-paused'); });

    film.addEventListener('mousemove', wake);
    film.addEventListener('touchstart', wake, { passive: true });

    if (toggle) {
      toggle.addEventListener('click', function () {
        if (video.paused) play(); else video.pause();
      });
    }

    function seek(clientX) {
      var r = track.getBoundingClientRect();
      var pct = Math.max(0, Math.min(1, (clientX - r.left) / r.width));
      if (video.duration) video.currentTime = pct * video.duration;
    }
    if (track) {
      var dragging = false;
      track.addEventListener('pointerdown', function (e) { dragging = true; seek(e.clientX); });
      window.addEventListener('pointermove', function (e) { if (dragging) seek(e.clientX); });
      window.addEventListener('pointerup', function () { dragging = false; });
      track.addEventListener('keydown', function (e) {
        if (!video.duration) return;
        if (e.key === 'ArrowRight') video.currentTime = Math.min(video.duration, video.currentTime + 5);
        if (e.key === 'ArrowLeft') video.currentTime = Math.max(0, video.currentTime - 5);
      });
    }

    if (mute) {
      mute.addEventListener('click', function () {
        video.muted = !video.muted;
        mute.textContent = video.muted ? mute.dataset.off : mute.dataset.on;
      });
    }

    if (full) {
      full.addEventListener('click', function () {
        var el = film;
        if (document.fullscreenElement) { document.exitFullscreen(); return; }
        if (el.requestFullscreen) el.requestFullscreen();
        else if (video.webkitEnterFullscreen) video.webkitEnterFullscreen();
      });
    }
  }

  function init(root) {
    root = root || document;
    each(root.querySelectorAll('[data-kp4-section]'), function (section) {
      initGallery(section);
      initDelivery(section);
      initSticky(section);
      initBuyPrice(section);
      initLoox(section, function () { initReviews(section); });
      initRails(section);
      initFilm(section);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(); });
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', function (e) {
    init(e.target);
  });
})();
